import { parse } from 'yaml';

import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { getLastNDays, getMonthToDate } from '../utils/timeRangePresets';
import {
  PlatformUsageConfig,
  ResolvedPlatformUsageConfig,
  ResolvedTimeRangePreset,
  TimeRangePresetConfig,
} from './types';

/**
 * Default configuration used as fallback if YAML loading fails.
 * Matches the current hardcoded behavior.
 */
export const DEFAULT_CONFIG: PlatformUsageConfig = {
  timeRangePresets: [
    { key: '30days-daily', label: '30 Days (Daily)', daysBack: 30, granularity: 'DAY' },
    { key: '3months-daily', label: '3 Months (Daily)', daysBack: 90, granularity: 'DAY' },
    { key: '7days-daily', label: '7 Days (Daily)', daysBack: 7, granularity: 'DAY' },
    { key: 'mtd-daily', label: 'MTD (Daily)', type: 'mtd', granularity: 'DAY' },
    { key: '1day-hourly', label: '1 Day (Hourly)', daysBack: 1, granularity: 'HOUR' },
  ],
  timing: {
    stage2DelayMs: 1000,
    customRangeDelayMs: 1000,
    cacheDurationMs: 1800000, // 30 minutes
    retryAttempts: 3,
    retryMaxDelayMs: 30000,
  },
  groupingStrategy: {
    immediateGroupings: ['WORKSPACE', 'USER'],
    delayedGroupings: ['SERVICE', 'TAG'],
  },
};

/**
 * Maps string granularity from YAML to Granularity enum value.
 */
function mapGranularity(granularityStr: string): Granularity {
  const mapping: Record<string, Granularity> = {
    HOUR: Granularity.HOUR,
    DAY: Granularity.DAY,
    WEEK: Granularity.WEEK,
    MONTH: Granularity.MONTH,
    YEAR: Granularity.YEAR,
  };
  const result = mapping[granularityStr.toUpperCase()];
  if (!result) {
    console.warn(`[PlatformUsageConfig] Unknown granularity "${granularityStr}", defaulting to DAY`);
    return Granularity.DAY;
  }
  return result;
}

/**
 * Maps string grouping from YAML to Grouping enum value.
 */
function mapGrouping(groupingStr: string): Grouping {
  const mapping: Record<string, Grouping> = {
    WORKSPACE: Grouping.WORKSPACE,
    USER: Grouping.USER,
    SERVICE: Grouping.SERVICE,
    TAG: Grouping.TAG,
  };
  const result = mapping[groupingStr.toUpperCase()];
  if (!result) {
    console.warn(`[PlatformUsageConfig] Unknown grouping "${groupingStr}", defaulting to WORKSPACE`);
    return Grouping.WORKSPACE;
  }
  return result;
}

/**
 * Resolves a preset config to actual dates based on daysBack or type.
 */
function resolvePreset(preset: TimeRangePresetConfig): ResolvedTimeRangePreset {
  let dateRange: { startTime: Date; endTime: Date };

  if (preset.type === 'mtd') {
    dateRange = getMonthToDate();
  } else if (preset.daysBack !== undefined) {
    dateRange = getLastNDays(preset.daysBack);
  } else {
    console.warn(`[PlatformUsageConfig] Preset "${preset.key}" has neither daysBack nor type, defaulting to 30 days`);
    dateRange = getLastNDays(30);
  }

  return {
    key: preset.key,
    label: preset.label,
    startTime: dateRange.startTime,
    endTime: dateRange.endTime,
    granularity: mapGranularity(preset.granularity),
  };
}

/**
 * Validates the configuration structure from YAML.
 */
function validateConfig(config: unknown): config is PlatformUsageConfig {
  if (typeof config !== 'object' || config === null) {
    console.error('[PlatformUsageConfig] Config must be an object');
    return false;
  }

  const c = config as PlatformUsageConfig;

  // Validate timeRangePresets
  if (!Array.isArray(c.timeRangePresets) || c.timeRangePresets.length === 0) {
    console.error('[PlatformUsageConfig] timeRangePresets must be a non-empty array');
    return false;
  }

  for (const preset of c.timeRangePresets) {
    if (!preset.key || !preset.label || !preset.granularity) {
      console.error('[PlatformUsageConfig] Each preset must have key, label, and granularity');
      return false;
    }
    if (preset.daysBack === undefined && preset.type === undefined) {
      console.error(`[PlatformUsageConfig] Preset "${preset.key}" must have either daysBack or type`);
      return false;
    }
  }

  // Validate timing
  if (typeof c.timing !== 'object' || c.timing === null) {
    console.error('[PlatformUsageConfig] timing must be an object');
    return false;
  }

  const requiredTimingFields = [
    'stage2DelayMs',
    'customRangeDelayMs',
    'cacheDurationMs',
    'retryAttempts',
    'retryMaxDelayMs',
  ];
  for (const field of requiredTimingFields) {
    if (typeof c.timing[field as keyof typeof c.timing] !== 'number') {
      console.error(`[PlatformUsageConfig] timing.${field} must be a number`);
      return false;
    }
  }

  // Validate groupingStrategy
  if (typeof c.groupingStrategy !== 'object' || c.groupingStrategy === null) {
    console.error('[PlatformUsageConfig] groupingStrategy must be an object');
    return false;
  }

  if (!Array.isArray(c.groupingStrategy.immediateGroupings) || !Array.isArray(c.groupingStrategy.delayedGroupings)) {
    console.error('[PlatformUsageConfig] groupingStrategy must have immediateGroupings and delayedGroupings arrays');
    return false;
  }

  return true;
}

/**
 * Resolves raw config from YAML to runtime config with typed enums and computed dates.
 */
export function resolveConfig(config: PlatformUsageConfig): ResolvedPlatformUsageConfig {
  const immediateGroupings = config.groupingStrategy.immediateGroupings.map(mapGrouping);
  const delayedGroupings = config.groupingStrategy.delayedGroupings.map(mapGrouping);

  return {
    timeRangePresets: config.timeRangePresets.map(resolvePreset),
    timing: { ...config.timing },
    groupingStrategy: {
      immediateGroupings,
      delayedGroupings,
      allGroupings: [...immediateGroupings, ...delayedGroupings],
    },
  };
}

/**
 * Fetches and parses the YAML configuration file from public folder.
 * Returns default config if fetch fails or config is invalid.
 */
export async function loadPlatformUsageConfig(): Promise<ResolvedPlatformUsageConfig> {
  try {
    const response = await fetch('/platform-usage-config.yaml');

    if (!response.ok) {
      console.warn(`[PlatformUsageConfig] Failed to load config (HTTP ${response.status}), using defaults`);
      return resolveConfig(DEFAULT_CONFIG);
    }

    const yamlText = await response.text();
    const parsed = parse(yamlText);

    if (!validateConfig(parsed)) {
      console.warn('[PlatformUsageConfig] Invalid config structure, using defaults');
      return resolveConfig(DEFAULT_CONFIG);
    }

    return resolveConfig(parsed);
  } catch (error) {
    console.error('[PlatformUsageConfig] Error loading config:', error);
    return resolveConfig(DEFAULT_CONFIG);
  }
}

/**
 * Returns resolved default config synchronously.
 * Used for initial state before async config loads.
 */
export function getDefaultResolvedConfig(): ResolvedPlatformUsageConfig {
  return resolveConfig(DEFAULT_CONFIG);
}
